import { useCallback, useEffect, useRef } from 'react';

// ----------------------------------------------------------------------
// Canvas de desenho livre sobre a imagem. Strokes em coordenadas
// normalizadas (0..1) para redesenho na resolução nativa no export final.

const BRUSH_SIZE = 0.006; // fração da largura da imagem

export default function DrawingCanvas({ strokes, color, onStrokesChange, readOnly = false }) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const currentStrokeRef = useRef(null);

    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const all = currentStrokeRef.current ? [...strokes, currentStrokeRef.current] : strokes;
        for (const stroke of all) {
            if (!stroke?.points?.length) continue;
            ctx.strokeStyle = stroke.color;
            ctx.lineWidth = Math.max(1, (stroke.size || BRUSH_SIZE) * canvas.width);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(stroke.points[0].x * canvas.width, stroke.points[0].y * canvas.height);
            for (let i = 1; i < stroke.points.length; i += 1) {
                ctx.lineTo(stroke.points[i].x * canvas.width, stroke.points[i].y * canvas.height);
            }
            ctx.stroke();
        }
    }, [strokes]);

    useEffect(() => {
        redraw();
    }, [redraw]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => redraw());
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [redraw]);

    const getPoint = (event) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
            y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
        };
    };

    const handlePointerDown = (event) => {
        event.preventDefault();
        canvasRef.current.setPointerCapture(event.pointerId);
        drawingRef.current = true;
        currentStrokeRef.current = { color, size: BRUSH_SIZE, points: [getPoint(event)] };
        redraw();
    };

    const handlePointerMove = (event) => {
        if (!drawingRef.current || !currentStrokeRef.current) return;
        currentStrokeRef.current.points.push(getPoint(event));
        redraw();
    };

    const handlePointerUp = () => {
        if (!drawingRef.current || !currentStrokeRef.current) return;
        drawingRef.current = false;
        const stroke = currentStrokeRef.current;
        currentStrokeRef.current = null;
        onStrokesChange([...strokes, stroke]);
    };

    if (readOnly) {
        return (
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
            />
        );
    }

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full cursor-crosshair"
            style={{ touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
        />
    );
}
